<template>
  <!-- Floating Button -->
  <button
    v-if="!store.isOpen"
    @click="store.open()"
    class="feedback-widget-button fixed bottom-6 right-6 w-12 h-12 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 flex items-center justify-center transition-all hover:scale-105 z-50"
    title="Share Feedback"
  >
    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
      <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  </button>

  <!-- Feedback Panel -->
  <div
    v-if="store.isOpen"
    class="feedback-widget-panel fixed bottom-6 right-6 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 flex flex-col overflow-hidden"
    style="max-height: 80vh"
  >
    <!-- Header -->
    <div class="feedback-widget-header flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-indigo-600 text-white">
      <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
      <span class="text-sm font-medium flex-1">Share Feedback</span>
      <button @click="store.close()" class="text-white/80 hover:text-white">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- IDENTIFY STEP -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-if="store.step === 'identify'" class="feedback-widget-identify flex flex-col gap-3 px-4 py-4 overflow-y-auto">
      <p class="text-sm text-gray-600">Let us know who you are so we can follow up.</p>

      <div>
        <label class="text-xs font-medium text-gray-600">Your email *</label>
        <input
          v-model="store.userEmail"
          type="email"
          placeholder="you@example.com"
          class="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 mt-1"
          @keyup.enter="store.proceedFromIdentify()"
        />
      </div>

      <div>
        <label class="text-xs font-medium text-gray-600">Name (optional)</label>
        <input
          v-model="store.userName"
          type="text"
          placeholder="Your name"
          class="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 mt-1"
        />
      </div>

      <div>
        <label class="text-xs font-medium text-gray-600">Product *</label>
        <select
          v-model="store.selectedProductId"
          class="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 mt-1"
        >
          <option :value="null" disabled>Select a product...</option>
          <option
            v-for="product in products"
            :key="product.id"
            :value="product.id"
          >
            {{ product.displayName }}
          </option>
        </select>
      </div>

      <button
        @click="store.proceedFromIdentify()"
        :disabled="!store.canProceedFromIdentify"
        class="w-full text-sm py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium mt-1"
      >
        Continue
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- READY STEP (voice-first) -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else-if="store.step === 'ready'" class="feedback-widget-ready flex flex-col items-center gap-4 px-4 py-8">
      <p class="text-sm text-gray-500">What's on your mind?</p>

      <!-- Big mic button -->
      <button
        @click="store.startVoiceRecording()"
        class="feedback-widget-mic-button w-20 h-20 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
        </svg>
      </button>
      <p class="text-xs text-gray-400">Tap to speak</p>

      <!-- Screenshot thumbnails (if any captured previously) -->
      <div v-if="store.screenshots.length > 0" class="feedback-widget-screenshot-strip flex gap-2 flex-wrap">
        <div
          v-for="(ss, idx) in store.screenshots"
          :key="ss.timestamp"
          class="relative group"
        >
          <img :src="ss.dataUrl" class="w-12 h-9 object-cover rounded border border-gray-200" :alt="'Screenshot ' + (idx + 1)" />
          <button
            @click="store.removeScreenshot(idx)"
            class="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white rounded-full text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >&times;</button>
        </div>
      </div>

      <button
        @click="store.switchToTyping()"
        class="text-xs text-gray-400 hover:text-indigo-600 transition-colors"
      >
        Or type your feedback
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- RECORDING STEP -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else-if="store.step === 'recording'" class="feedback-widget-recording flex flex-col gap-3 px-4 py-4">
      <!-- Recording indicator -->
      <div class="feedback-widget-recording-header flex items-center gap-3">
        <span class="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
        <span class="text-sm font-medium text-red-700 flex-1">Recording</span>
        <span class="text-xs text-gray-500 font-mono">
          {{ formatDuration(store.recordingDuration) }} / {{ formatDuration(store.MAX_RECORDING_SECONDS) }}
        </span>
      </div>

      <!-- Audio level indicator -->
      <div class="feedback-widget-audio-level h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          class="h-full bg-red-500 rounded-full transition-all duration-75"
          :style="{ width: (store.currentAudioLevel * 100) + '%' }"
        ></div>
      </div>

      <!-- Live transcript -->
      <div class="feedback-widget-live-transcript bg-gray-50 rounded-md px-3 py-2 min-h-[80px] max-h-[120px] overflow-y-auto">
        <p v-if="store.liveTranscript.trim()" class="text-sm text-gray-700 leading-relaxed">
          {{ store.liveTranscript }}
        </p>
        <p v-else class="text-xs text-gray-400 italic">Listening...</p>
      </div>

      <!-- Actions during recording -->
      <div class="flex items-center gap-2">
        <button
          @click="store.captureScreenshot()"
          :disabled="store.screenshots.length >= store.MAX_SCREENSHOTS"
          class="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Screenshot
          <span v-if="store.screenshots.length > 0" class="text-gray-400">{{ store.screenshots.length }}/{{ store.MAX_SCREENSHOTS }}</span>
        </button>

        <div class="flex-1"></div>

        <button
          @click="store.stopVoiceRecording()"
          class="flex items-center gap-1.5 text-sm px-4 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
          Done
        </button>
      </div>

      <!-- Screenshot thumbnails -->
      <div v-if="store.screenshots.length > 0" class="feedback-widget-recording-screenshots flex gap-2 flex-wrap">
        <div
          v-for="(ss, idx) in store.screenshots"
          :key="ss.timestamp"
          class="relative group"
        >
          <img :src="ss.dataUrl" class="w-12 h-9 object-cover rounded border border-gray-200" :alt="'Screenshot ' + (idx + 1)" />
          <button
            @click="store.removeScreenshot(idx)"
            class="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white rounded-full text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >&times;</button>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- TYPING STEP (alternative input) -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else-if="store.step === 'typing'" class="feedback-widget-typing flex flex-col gap-3 px-4 py-4 overflow-y-auto">
      <div>
        <label class="text-xs font-medium text-gray-600">What's on your mind?</label>
        <textarea
          v-model="store.transcript"
          placeholder="Describe a bug, suggest a feature, or share feedback..."
          rows="5"
          class="w-full text-sm border border-gray-300 rounded-md px-3 py-2 mt-1 resize-none"
        ></textarea>
        <p class="text-xs text-gray-400 mt-0.5">{{ store.transcript.length }} characters</p>
      </div>

      <!-- Screenshot capture -->
      <div class="flex items-center gap-2">
        <button
          @click="store.captureScreenshot()"
          :disabled="store.screenshots.length >= store.MAX_SCREENSHOTS"
          class="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Screenshot
          <span v-if="store.screenshots.length > 0" class="text-gray-400">{{ store.screenshots.length }}/{{ store.MAX_SCREENSHOTS }}</span>
        </button>
      </div>

      <!-- Screenshot thumbnails -->
      <div v-if="store.screenshots.length > 0" class="flex gap-2 flex-wrap">
        <div
          v-for="(ss, idx) in store.screenshots"
          :key="ss.timestamp"
          class="relative group"
        >
          <img :src="ss.dataUrl" class="w-16 h-12 object-cover rounded border border-gray-200" :alt="'Screenshot ' + (idx + 1)" />
          <button
            @click="store.removeScreenshot(idx)"
            class="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >&times;</button>
        </div>
      </div>

      <!-- Error -->
      <p v-if="store.error" class="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-md">
        {{ store.error }}
      </p>

      <div class="flex items-center gap-2 mt-1">
        <button
          @click="store.switchToVoice()"
          class="text-xs text-gray-400 hover:text-indigo-600 transition-colors"
        >
          Switch to voice
        </button>
        <div class="flex-1"></div>
        <button
          @click="store.proceedFromTyping()"
          :disabled="!store.transcript.trim()"
          class="text-sm px-4 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          Continue
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- TRANSCRIBING STEP (AI analysis in progress) -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else-if="store.step === 'transcribing'" class="feedback-widget-transcribing flex flex-col items-center gap-3 px-4 py-8">
      <div class="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      <p class="text-sm text-gray-600">Analyzing your feedback...</p>
      <p class="text-xs text-gray-400">This takes just a moment</p>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- REVIEW STEP (AI-structured preview) -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else-if="store.step === 'review'" class="feedback-widget-review flex flex-col gap-3 px-4 py-4 overflow-y-auto">
      <!-- AI Preview Card -->
      <div v-if="store.aiPreview" class="feedback-widget-ai-preview bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-3">
        <p class="text-xs font-medium text-indigo-700 mb-2">Here's what we understood:</p>

        <!-- Summary -->
        <p class="text-sm text-gray-800 leading-relaxed">{{ store.aiPreview.summary }}</p>

        <!-- Type + Sentiment badges -->
        <div class="flex items-center gap-2 mt-2 flex-wrap">
          <span
            class="text-xs px-2 py-0.5 rounded-full font-medium"
            :class="getTypeClass(store.aiPreview.type)"
          >
            {{ formatType(store.aiPreview.type) }}
          </span>
          <span
            class="text-xs px-2 py-0.5 rounded-full"
            :class="getSentimentClass(store.aiPreview.sentiment)"
          >
            {{ store.aiPreview.sentiment }}
          </span>
          <span
            class="text-xs px-2 py-0.5 rounded-full"
            :class="getUrgencyClass(store.aiPreview.urgency)"
          >
            {{ store.aiPreview.urgency }}
          </span>
          <span class="text-xs text-gray-400 ml-auto">
            {{ Math.round(store.aiPreview.confidence * 100) }}% confidence
          </span>
        </div>

        <!-- Action items -->
        <div v-if="store.aiPreview.actionItems.length > 0" class="mt-2">
          <p class="text-xs font-medium text-gray-600 mb-1">Action items:</p>
          <ul class="text-xs text-gray-600 space-y-0.5">
            <li v-for="(item, idx) in store.aiPreview.actionItems" :key="idx" class="flex items-start gap-1.5">
              <span class="text-indigo-400 mt-0.5">-</span>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- No AI preview fallback -->
      <div v-else class="bg-gray-50 border border-gray-200 rounded-lg px-3 py-3">
        <p class="text-xs text-gray-500">AI analysis unavailable. Your feedback will still be submitted and analyzed.</p>
      </div>

      <!-- Editable transcript -->
      <div>
        <button
          @click="showTranscript = !showTranscript"
          class="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 transition-transform" :class="{ 'rotate-90': showTranscript }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          {{ showTranscript ? 'Hide' : 'Show' }} full transcript
        </button>
        <textarea
          v-if="showTranscript"
          v-model="store.transcript"
          rows="3"
          class="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 mt-1 resize-none text-gray-600"
        ></textarea>
      </div>

      <!-- Add clarification -->
      <div class="feedback-widget-clarify">
        <div class="flex items-center gap-2">
          <input
            v-model="clarificationText"
            placeholder="Add more details..."
            class="flex-1 text-xs border border-gray-300 rounded-md px-2 py-1.5"
            @keyup.enter="handleAddClarification"
          />
          <button
            @click="handleAddClarification"
            :disabled="!clarificationText.trim()"
            class="text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      <!-- Screenshot thumbnails -->
      <div class="flex items-center gap-2">
        <button
          @click="store.captureScreenshot()"
          :disabled="store.screenshots.length >= store.MAX_SCREENSHOTS"
          class="flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 disabled:opacity-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Add screenshot
        </button>
        <div v-if="store.screenshots.length > 0" class="flex gap-1.5">
          <div v-for="(ss, idx) in store.screenshots" :key="ss.timestamp" class="relative group">
            <img :src="ss.dataUrl" class="w-10 h-7 object-cover rounded border border-gray-200" :alt="'Screenshot ' + (idx + 1)" />
            <button
              @click="store.removeScreenshot(idx)"
              class="absolute -top-1 -right-1 w-3 h-3 bg-red-500 text-white rounded-full text-[7px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >&times;</button>
          </div>
        </div>
      </div>

      <!-- Audio playback -->
      <div v-if="store.audioPreviewUrl" class="flex items-center gap-2 bg-gray-50 rounded-md px-2 py-1.5">
        <audio :src="store.audioPreviewUrl" controls class="h-7 flex-1" style="max-width: 260px;"></audio>
      </div>

      <!-- Error -->
      <p v-if="store.error" class="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-md">
        {{ store.error }}
      </p>

      <!-- Actions -->
      <div class="flex items-center gap-2 mt-1">
        <button
          @click="store.reset()"
          class="text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          Start over
        </button>
        <div class="flex-1"></div>
        <button
          @click="submitFeedback"
          :disabled="!store.canSubmit || submitting"
          class="text-sm px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {{ submitting ? 'Submitting...' : 'Submit Feedback' }}
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- SUBMITTING STEP -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else-if="store.step === 'submitting'" class="feedback-widget-submitting flex flex-col items-center gap-3 px-4 py-8">
      <div class="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      <p class="text-sm text-gray-600">{{ uploadStatus }}</p>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- SUCCESS STEP -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else-if="store.step === 'success'" class="feedback-widget-success flex flex-col items-center gap-3 px-4 py-8">
      <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p class="text-sm font-medium text-gray-900">Thank you!</p>
      <p class="text-xs text-gray-500 text-center">Your feedback has been submitted and will be analyzed by AI.</p>
      <button
        @click="handleDone"
        class="text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-2"
      >
        Close
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- ERROR STEP -->
    <!-- ═══════════════════════════════════════════════ -->
    <div v-else-if="store.step === 'error'" class="feedback-widget-error flex flex-col items-center gap-3 px-4 py-8">
      <div class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <p class="text-sm font-medium text-gray-900">Something went wrong</p>
      <p class="text-xs text-gray-500 text-center">{{ store.error || 'Please try again.' }}</p>
      <button
        @click="store.step = 'review'"
        class="text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-2"
      >
        Try again
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import apiClient from '../../api/client'
import { useFeedbackStore } from '../../stores/feedbackStore'
import type { FeedbackWidgetConfig, PreviewResult } from '../../stores/feedbackStore'

declare global {
  interface Window {
    __feedbackConfig?: FeedbackWidgetConfig
  }
}

const store = useFeedbackStore()
const submitting = ref(false)
const uploadStatus = ref('Submitting your feedback...')
const showTranscript = ref(false)
const clarificationText = ref('')
const products = ref<any[]>([])

// Fetch products on mount
onMounted(async () => {
  if (window.__feedbackConfig) {
    store.initFromHost(window.__feedbackConfig)
  }
  try {
    products.value = await apiClient.products.list()
  } catch (_err) {
    console.error('[Feedback] Failed to load products')
  }
})

// Watch for step transitions to trigger preview analysis
watch(
  () => store.step,
  async (newStep) => {
    if (newStep === 'transcribing') {
      await store.requestPreview(callPreviewAnalyze)
    }
  }
)

async function callPreviewAnalyze(args: { transcript: string }): Promise<PreviewResult> {
  return await apiClient.analysis.preview(args)
}

async function uploadFile(blob: Blob, folder: string): Promise<string> {
  const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'mp4' : blob.type.includes('png') ? 'png' : 'webp'
  const filename = `${Date.now()}.${ext}`

  // Step 1: Get presigned upload URL
  const { url, key } = await apiClient.storage.getUploadUrl({
    filename,
    contentType: blob.type,
    folder,
  })

  // Step 2: Upload file directly to S3
  await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': blob.type },
    body: blob,
  })

  // Step 3: Get presigned download URL
  const { url: fileUrl } = await apiClient.storage.getFileUrl(key)
  return fileUrl
}

async function submitFeedback() {
  if (!store.canSubmit) return

  submitting.value = true
  store.step = 'submitting'

  try {
    store.captureMetadata()

    let audioUrl: string | undefined = undefined
    let screenshotUrls: string[] = []

    if (store.audioBlob) {
      uploadStatus.value = 'Uploading audio...'
      audioUrl = await uploadFile(store.audioBlob, 'audio')
    }

    if (store.screenshots.length > 0) {
      uploadStatus.value = 'Uploading screenshots...'
      for (let i = 0; i < store.screenshots.length; i++) {
        uploadStatus.value = `Uploading screenshot ${i + 1}/${store.screenshots.length}...`
        const url = await uploadFile(store.screenshots[i].blob, 'screenshots')
        screenshotUrls.push(url)
      }
    }

    uploadStatus.value = 'Submitting feedback...'

    await apiClient.feedback.create({
      productId: store.selectedProductId!,
      userId: store.userEmail,
      transcript: store.transcript.trim() || '(voice feedback)',
      audioUrl,
      screenshotUrls,
      pageUrl: store.metadata.pageUrl,
      pageTitle: store.metadata.pageTitle,
      browserInfo: store.metadata.browserInfo,
      deviceType: store.metadata.deviceType,
      screenResolution: store.metadata.screenResolution,
      sessionDuration: store.metadata.sessionDuration,
      navigationPath: store.metadata.navigationPath,
      userEmail: store.userEmail.trim(),
      userPreviousFeedbackCount: 0,
      inputMode: store.inputMode,
      userName: store.userName.trim() || undefined,
    })

    store.step = 'success'
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to submit feedback'
    store.error = message
    store.step = 'error'
  } finally {
    submitting.value = false
  }
}

function handleDone() {
  store.reset()
  store.close()
}

function handleAddClarification() {
  if (clarificationText.value.trim()) {
    store.addClarification(clarificationText.value)
    clarificationText.value = ''
  }
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function getTypeClass(type: string): string {
  const classes: Record<string, string> = {
    bug: 'bg-red-100 text-red-700',
    feature_request: 'bg-indigo-100 text-indigo-700',
    improvement: 'bg-teal-100 text-teal-700',
    support: 'bg-amber-100 text-amber-700',
    praise: 'bg-green-100 text-green-700',
    confusion: 'bg-orange-100 text-orange-700',
  }
  return classes[type] || 'bg-gray-100 text-gray-600'
}

function formatType(type: string): string {
  return type.replace(/_/g, ' ')
}

function getSentimentClass(sentiment: string): string {
  const classes: Record<string, string> = {
    frustrated: 'bg-red-50 text-red-600',
    neutral: 'bg-gray-100 text-gray-600',
    positive: 'bg-green-50 text-green-600',
  }
  return classes[sentiment] || 'bg-gray-100 text-gray-600'
}

function getUrgencyClass(urgency: string): string {
  const classes: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-gray-100 text-gray-600',
  }
  return classes[urgency] || 'bg-gray-100 text-gray-600'
}
</script>
